import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Global UI state only. Anything that comes from the server belongs in
 * react-query, not here.
 */
interface UiState {
  /**
   * Mirror of the admin session cookie. The cookie itself is HttpOnly, so the
   * SPA cannot read it; this flag is set from Login / WhoAmI and is only used
   * to decide whether to render the shell or bounce to /login.
   */
  authenticated: boolean;
  /** ISO string; `undefined` when unknown. */
  sessionExpiresAt: string | undefined;
  /** Account the traffic screens are scoped to. */
  selectedAccountId: string | undefined;
  sidebarCollapsed: boolean;

  setAuthenticated: (authenticated: boolean, expiresAt?: Date) => void;
  setSelectedAccountId: (accountId: string | undefined) => void;
  toggleSidebar: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      authenticated: false,
      sessionExpiresAt: undefined,
      selectedAccountId: undefined,
      sidebarCollapsed: false,

      setAuthenticated: (authenticated, expiresAt) =>
        set({
          authenticated,
          sessionExpiresAt: authenticated ? expiresAt?.toISOString() : undefined,
        }),
      setSelectedAccountId: (selectedAccountId) => set({ selectedAccountId }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
    }),
    {
      name: "wabms.ui",
      partialize: (s) => ({
        authenticated: s.authenticated,
        sessionExpiresAt: s.sessionExpiresAt,
        selectedAccountId: s.selectedAccountId,
        sidebarCollapsed: s.sidebarCollapsed,
      }),
    },
  ),
);
